import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Sparkle, Rocket, Lightning, ArrowUpRight, CrownSimple } from "@phosphor-icons/react";
import { api, ensureUserId, getName } from "@/lib/api";

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const item = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

export default function Home() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const uid = ensureUserId();
    api.get(`/dashboard/${uid}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="home-loading">
        <div className="w-2 h-2 rounded-full bg-white/40 animate-pulse" />
      </div>
    );
  }

  const xpPct = Math.round((data.xp / data.next_level_xp) * 100);

  return (
    <div className="mesh-bg min-h-screen pb-32" data-testid="home-screen">
      {/* Header */}
      <div className="px-6 pt-12 pb-4 flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-zinc-500">Welcome back</p>
          <h1 className="font-display text-3xl font-light tracking-tight">{getName() || data.name}.</h1>
        </div>
        <Link
          to="/membership"
          data-testid="home-crown"
          className="w-11 h-11 rounded-full glass flex items-center justify-center active:scale-95 transition"
        >
          <CrownSimple size={20} weight="fill" className="text-amber-300" />
        </Link>
      </div>

      <motion.div variants={stagger} initial="initial" animate="animate" className="px-6 space-y-4">
        {/* Streak + XP bento */}
        <motion.div variants={item} className="grid grid-cols-3 gap-3">
          <div className="col-span-2 glass rounded-3xl p-5 relative overflow-hidden" data-testid="card-xp">
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-indigo-500/20 blur-3xl" />
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Founder Path · {data.tier}</p>
            <div className="mt-2 flex items-end gap-2">
              <span className="font-display text-4xl font-light">{data.xp.toLocaleString()}</span>
              <span className="text-zinc-500 text-sm pb-1">/ {data.next_level_xp.toLocaleString()} XP</span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${xpPct}%` }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-violet-300"
              />
            </div>
          </div>
          <div className="glass rounded-3xl p-5 flex flex-col justify-between" data-testid="card-streak">
            <Flame size={20} weight="fill" className="text-amber-300" />
            <div>
              <div className="font-display text-3xl font-light">{data.streak_days}</div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Day streak</p>
            </div>
          </div>
        </motion.div>

        {/* Scores */}
        <motion.div variants={item} className="grid grid-cols-2 gap-3">
          <ScoreCard label="Founder Score" value={data.founder_score} icon={<Rocket size={16} weight="fill" />} accent="#818CF8" />
          <ScoreCard label="AI Skill" value={data.ai_skill_score} icon={<Sparkle size={16} weight="fill" />} accent="#60A5FA" />
        </motion.div>

        {/* Daily quests */}
        <motion.div variants={item}>
          <SectionLabel title="Daily quests" right="3 of 5" />
          <div className="space-y-2">
            {data.daily_quests.map((q) => (
              <div key={q.id} className="glass rounded-2xl p-4 flex items-center gap-3 active:scale-[0.99] transition" data-testid={`quest-${q.id}`}>
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <Lightning size={18} weight="fill" className="text-amber-300" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{q.title}</div>
                  <div className="text-[11px] text-zinc-500">{q.category}</div>
                </div>
                <div className="text-xs font-mono-jb text-amber-300">+{q.xp} XP</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Active journeys */}
        <motion.div variants={item}>
          <SectionLabel title="Continue learning" />
          <div className="space-y-3">
            {data.active_journeys.map((j) => {
              const m = data.recommended_mentors.find((x) => x.id === j.mentor_id);
              return (
                <Link key={j.id} to={`/chat/${j.mentor_id}`} className="block glass rounded-2xl p-4" data-testid={`journey-${j.id}`}>
                  <div className="flex items-center gap-3">
                    {m && <img src={m.avatar} alt={m.name} className="w-12 h-12 rounded-2xl mentor-thumb" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{m?.title}</div>
                      <div className="font-medium text-sm truncate">{j.title}</div>
                    </div>
                    <ArrowUpRight size={16} className="text-zinc-400" />
                  </div>
                  <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-white" style={{ width: `${Math.round(j.progress * 100)}%` }} />
                  </div>
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* Recommended mentors */}
        <motion.div variants={item}>
          <SectionLabel title="Mentors for you" right={<Link to="/mentors" className="text-xs text-zinc-400" data-testid="see-all-mentors">See all</Link>} />
          <div className="flex gap-3 overflow-x-auto -mx-6 px-6 pb-1 snap-x">
            {data.recommended_mentors.map((m) => (
              <Link
                key={m.id}
                to={`/mentors/${m.id}`}
                data-testid={`rec-mentor-${m.id}`}
                className="snap-start shrink-0 w-44 glass rounded-3xl overflow-hidden active:scale-[0.98] transition"
              >
                <div className="h-32 relative overflow-hidden">
                  <img src={m.avatar} alt={m.name} className="w-full h-full mentor-portrait" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/70">{m.domain}</div>
                    <div className="text-sm font-medium leading-tight">{m.name}</div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-[11px] text-zinc-400 line-clamp-2 leading-snug">{m.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Trending challenges */}
        <motion.div variants={item}>
          <SectionLabel title="Trending challenges" />
          <div className="grid grid-cols-2 gap-3">
            {data.trending_challenges.map((c) => (
              <button
                key={c.id}
                onClick={() => nav("/play")}
                data-testid={`challenge-${c.id}`}
                className="glass rounded-2xl p-4 text-left active:scale-[0.98] transition relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-50" style={{ background: c.color }} />
                <div className="relative">
                  <div className="text-sm font-medium">{c.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">{c.joined.toLocaleString()} joined</div>
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

const ScoreCard = ({ label, value, icon, accent }) => (
  <div className="glass rounded-2xl p-4 relative overflow-hidden">
    <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full blur-2xl" style={{ background: accent, opacity: 0.18 }} />
    <div className="flex items-center gap-2 text-zinc-400">
      <span style={{ color: accent }}>{icon}</span>
      <span className="text-[10px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="mt-2 flex items-end gap-1">
      <span className="font-display text-3xl font-light">{value}</span>
      <span className="text-zinc-500 text-xs pb-1">/100</span>
    </div>
  </div>
);

const SectionLabel = ({ title, right }) => (
  <div className="flex items-center justify-between mt-2 mb-3">
    <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">{title}</h3>
    {typeof right === "string" ? <span className="text-[11px] text-zinc-500">{right}</span> : right}
  </div>
);
